import type { HTMLAttributes, Ref } from "react";
import { createElement } from "react";
import { cn } from "@rosette/ts/src/util/cn.js";


/**
 * brand is the site's own surface: dialogs, game menus, account forms.
 * reading is for text: articles, blog sections, settings.
 * neutral is for content with colours of its own, such as the premium tiers.
 * menu is neutral with a gradient and a glow, for the game's intro and
 * loading panels over the board.
 */
export type CardTone = "brand" | "reading" | "neutral" | "menu";

const TONE_CLASSES: Record<CardTone, string> = {
    brand: "border-b-4 border-b-shade bg-surface",
    reading: "bg-surface-reading",
    neutral: "border-b-4 border-b-shade bg-surface-neutral",
    menu: cn(
        "border-b-4 border-b-surface-sunken bg-linear-to-b from-surface-neutral to-surface-reading",
        "shadow-[0_4px_8px_rgb(0_0_0/0.3),0_0_20px_rgb(0_0_0/0.5)]",
    ),
};


interface CardProps extends HTMLAttributes<HTMLElement> {
    as?: "div" | "section" | "article";
    tone?: CardTone;
    ref?: Ref<HTMLElement>;
}


/**
 * An opaque surface raised above the page, holding one self-contained
 * piece of content.
 */
export function Card({
    as = "div",
    tone = "brand",
    className,
    ...props
}: CardProps) {
    return createElement(as, {
        ...props,
        className: cn(
            "w-full rounded-2xl p-4 text-left text-body shadow-md md:p-6",
            "*:first:mt-0 *:last:mb-0",
            TONE_CLASSES[tone],
            className ?? null,
        ),
    });
}
